import { Helmet } from "react-helmet-async";
import { useEffect, useMemo, useState } from "react";
import { APP_FAVICON_URL, APP_LOGO_URL } from "@/constants/branding";
import {
  getPublicSiteSettings,
  type SiteSettings,
} from "@/services/siteSettings";

interface SEOMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  structuredData?: any | any[];
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: string;
  noIndex?: boolean;
  robots?: string; // Custom robots directive (e.g., "index, follow, max-image-preview:large")
  breadcrumbs?: Array<{ name: string; url: string }>;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  skipCanonical?: boolean; // Skip canonical/og:url rendering (for DynamicConvertPage routes)
}

export default function SEOMeta({
  title,
  description,
  keywords,
  canonical,
  structuredData,
  ogImage = "/docpose-cloud-og.png",
  ogImageAlt,
  ogType = "website",
  noIndex = false,
  robots,
  breadcrumbs,
  author,
  publishedTime,
  modifiedTime,
  skipCanonical = false,
}: SEOMetaProps) {
  const [site, setSite] = useState<SiteSettings | null>(null);

  useEffect(() => {
    getPublicSiteSettings()
      .then(setSite)
      .catch(() => setSite(null));
  }, []);

  const siteName = site?.siteName || "Docpose";
  const baseUrl =
    site?.baseUrl ||
    (typeof window !== "undefined"
      ? window.location.origin
      : "https://docpose.cloud");
  const twitter = site?.twitterHandle || "@docpose";
  const defaultOg = site?.ogImageUrl || "/docpose-cloud-og.png";
  const favicon = site?.faviconUrl || APP_FAVICON_URL;

  const formatTitle = (t: string) => {
    const pattern = site?.defaultMetaTitle;
    if (pattern && pattern.includes("{title}"))
      return pattern.replace("{title}", t);
    return t.includes(siteName) ? t : `${t} | ${siteName}`;
  };

  const absUrl = (u?: string | null) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    if (u.startsWith("/")) return `${baseUrl}${u}`;
    return `${baseUrl}/${u}`;
  };

  const fullTitle = formatTitle(title);
  const currentUrl =
    canonical ||
    (typeof window !== "undefined" ? window.location.href : baseUrl);
  const resolvedOg = ogImage ? absUrl(ogImage) : absUrl(defaultOg);

  // Note: breadcrumb and organization schemas are now included in the structuredData prop
  // passed by pages (as part of a single @graph JSON-LD). We no longer generate them here
  // to avoid duplicate JSON-LD scripts.

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {/* Canonical tag: Server-side handles DynamicConvertPage routes; React Helmet handles other routes */}
      {!skipCanonical && canonical && <link rel="canonical" href={canonical} />}
      {favicon && <link rel="icon" href={absUrl(favicon)} />}
      {/* Robots directive */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : robots ? (
        <meta name="robots" content={robots} />
      ) : null}

      {/* Author and Publication Info (optional) */}
      {author && <meta name="author" content={author} />}
      {publishedTime && (
        <meta name="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta name="article:modified_time" content={modifiedTime} />
      )}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {/* og:url: Server-side handles DynamicConvertPage routes; React Helmet handles other routes */}
      {!skipCanonical && canonical && (
        <meta property="og:url" content={canonical} />
      )}
      <meta property="og:image" content={resolvedOg} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedOg} />
      {ogImageAlt && <meta name="twitter:image:alt" content={ogImageAlt} />}
      <meta name="twitter:site" content={twitter} />
      <meta name="twitter:creator" content={twitter} />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#047857" />
      <meta name="msapplication-TileColor" content="#047857" />

      {/* Structured Data - Single JSON-LD script with @graph containing all schemas */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Verification Meta Tags for Production */}
      {!noIndex && import.meta.env.PROD && (
        <>
          {site?.googleSiteVerification && (
            <meta
              name="google-site-verification"
              content={site.googleSiteVerification}
            />
          )}
          {site?.msValidate && (
            <meta name="msvalidate.01" content={site.msValidate} />
          )}
          {site?.yandexVerification && (
            <meta
              name="yandex-verification"
              content={site.yandexVerification}
            />
          )}
        </>
      )}
    </Helmet>
  );
}
